'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useToast } from '@/components/ToastProvider';
import { CLIENT_CACHE_EVENTS } from '@/lib/client-request-cache';
import {
  claimFeaturedRewardNotice,
  dismissFeaturedRewardNotice,
  formatFeaturedRewardWorkId,
  getFeaturedRewardTarget,
  rememberFeaturedRewardReturnLocation,
} from '@/lib/social/featured-reward-notice';

export function useFeaturedRewardToast(rewards = [], markRead) {
  const locale = useLocale();
  const t = useTranslations('GateD.notifications');
  const { showToast } = useToast();
  const [pendingRewards, setPendingRewards] = useState([]);
  const [activeRewardId, setActiveRewardId] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const unreadRewardIds = new Set(rewards.map((reward) => reward?.id).filter(Boolean));
    const nextRewards = rewards.filter((reward) => (
      reward?.id && claimFeaturedRewardNotice(reward.id)
    ));
    setPendingRewards((current) => {
      const retained = current.filter((reward) => unreadRewardIds.has(reward.id));
      return nextRewards.length ? retained.concat(nextRewards) : retained;
    });
  }, [rewards]);

  const openReward = useCallback(async (reward) => {
    try {
      await markRead?.(reward.id);
    } finally {
      rememberFeaturedRewardReturnLocation(reward);
      const target = getFeaturedRewardTarget(reward);
      window.location.assign(`/${locale}${target}`);
    }
  }, [locale, markRead]);

  useEffect(() => {
    if (activeRewardId || pendingRewards.length === 0) return undefined;
    const reward = pendingRewards[0];
    const rewardCredits = Number(reward.payload?.rewardCredits || 50);
    setActiveRewardId(reward.id);
    showToast({
      title: t('featuredRewardTitle', { count: rewardCredits }),
      description: t('featuredRewardBody', { workId: formatFeaturedRewardWorkId(reward) }),
      type: 'reward',
      duration: 15000,
      actionLabel: t('featuredRewardAction'),
      onClick: () => void openReward(reward),
      onAction: () => void openReward(reward),
      onDismiss: ({ reason }) => {
        if (reason === 'manual') {
          dismissFeaturedRewardNotice(reward.id);
          void markRead?.(reward.id)?.catch(() => {});
        }
        if (!mountedRef.current) return;
        setPendingRewards((current) => current.filter((item) => item.id !== reward.id));
        setActiveRewardId(null);
      },
    });
    window.dispatchEvent(new Event(CLIENT_CACHE_EVENTS.creditsUpdated));
    window.dispatchEvent(new Event(CLIENT_CACHE_EVENTS.ownerAssetsUpdated));
    return undefined;
  }, [activeRewardId, markRead, openReward, pendingRewards, showToast, t]);
}
